import * as THREE from "three";

/**
 * A cheaper soft-shadow filter for the sun's shadow map.
 *
 * three.js (r169) stores shadow depth packed into RGBA8 and filters it by hand: every texel is a fetch plus an
 * unpack, with no hardware comparison. Its PCFSoftShadowMap takes SIXTEEN of those per pixel for every material that
 * receives shadow — the ground, which covers most of the screen, included — to build a smooth 3×3-texel penumbra.
 *
 * The sun's map is fitted to the view (SceneRig.fitShadowToView), so at 2048² one shadow texel is about half a screen
 * pixel at the default zoom and a pixel or so zoomed right in: a 3×3-texel penumbra is one or two pixels wide. Five
 * taps — the centre, weighted double, and four on a rotated grid a texel out — give the same edge width with six grey
 * levels across it, for a third of the fetches.
 *
 * The plain PCF filter (the lower tiers) and point-light shadows are left as they are.
 */
export function installCheapSoftShadows(): void {
  const chunk = THREE.ShaderChunk.shadowmap_pars_fragment;
  if (chunk.includes("TD_CHEAP_SOFT_SHADOW")) return;
  const begin = chunk.indexOf("#elif defined( SHADOWMAP_TYPE_PCF_SOFT )");
  const end = chunk.indexOf("#elif defined( SHADOWMAP_TYPE_VSM )", begin);
  if (begin < 0 || end < 0) return;                         // three changed its chunk: keep its own filter
  const cheap = `#elif defined( SHADOWMAP_TYPE_PCF_SOFT )
			// TD_CHEAP_SOFT_SHADOW (render/shadowFilter.ts)
			vec2 ts = vec2( 1.0 ) / shadowMapSize;
			shadow = (
				texture2DCompare( shadowMap, shadowCoord.xy, shadowCoord.z ) * 2.0 +
				texture2DCompare( shadowMap, shadowCoord.xy + vec2( 0.375, 1.125 ) * ts, shadowCoord.z ) +
				texture2DCompare( shadowMap, shadowCoord.xy + vec2( -1.125, 0.375 ) * ts, shadowCoord.z ) +
				texture2DCompare( shadowMap, shadowCoord.xy + vec2( -0.375, -1.125 ) * ts, shadowCoord.z ) +
				texture2DCompare( shadowMap, shadowCoord.xy + vec2( 1.125, -0.375 ) * ts, shadowCoord.z )
			) * ( 1.0 / 6.0 );

		`;
  THREE.ShaderChunk.shadowmap_pars_fragment = chunk.slice(0, begin) + cheap + chunk.slice(end);
}
